// @ts-nocheck
import { AccountType, TransactionTypeChar } from '@tassat/common-definitions';
import { UserAccountRecord } from '@tassat/fred-definitions';
import {
    AccountRecordExtended,
    ClientUserMapped,
    TransactionHistoryRecord,
    UserAccountMapped
} from '@tassat/fred-extended-definitions';
import { createSelector } from 'reselect';

import { MAX_SEARCH_RESULTS } from 'Lib/constant';
import { caseInsensitiveSort } from 'Lib/stringHelpers';
import { AdminState } from 'Reducers/store/adminStore';
import { ClientState } from 'Reducers/store/clientStore';
import { ClientRoleEnum } from 'SharedComponents/users/ClientRoleEnum';

import { getOpenWalletsArray, getOpenWalletssByFirmId } from './accounts';
import {
    getAccountRelationships, getAccounts, getFirms, getUser, getUserAccounts, getUsers
} from './common';

interface UserAccountMappedObj {
    [key: string]: UserAccountMapped[];
}

const getState = (state: AdminState | ClientState) => state;
const getFirmId = (_state: AdminState | ClientState, _userId: string,
    _includeDeleted: boolean | string, _pcgId: string, firmId: string) => firmId;
const getAccountId = (_state: AdminState | ClientState, _userId: string, accountId: string) => accountId;
const getUserId = (_state: AdminState | ClientState, userId: string, _accountId: string) => userId;
const getIsMakerApprover = (_state: AdminState | ClientState, _userId: string,
    _accountId: string, IsMakerApprover: boolean) => IsMakerApprover;
const getUserIdWithFirm = (_state: AdminState, _firmId: string, userId: string) => userId;
const getSort = (_state: AdminState | ClientState, _userId: string, _accountId: string, _includeClosed: boolean, sort: boolean) => sort;
const getIncludeClosed = (_state: AdminState | ClientState, _userId: string, _accountId: string, includeClosed: boolean) => includeClosed;
const getTransaction = (_state: AdminState | ClientState, transaction: TransactionHistoryRecord) => transaction;
const getPledgeDetail = (_: AdminState | ClientState, _selectedPledge: object, pledgeDetail: object) => pledgeDetail || {};
const getUserSearchString = (_state: AdminState | ClientState, _userId: string, _includeDeleted: boolean,
    _pcgId: string, _firmId: string, clientSearchValue: string) => clientSearchValue;
const getSenderAddress = (_state: AdminState | ClientState, _userId: string, _accountId: string, senderAddress: string) => senderAddress;
const getPledgeParticipantAccount = (_state: AdminState | ClientState, pledgeParticipantAccount: string) => pledgeParticipantAccount;

export const getMappedUserAccountsByUserIdAdmin = createSelector(
    [ getUserAccounts, getUser, getUsers, getAccounts, getFirms, getUserId, getIncludeClosed, getSort ],
    (userAccountsList, currentUser, users, accounts, firms, userId, includeClosed, sort): UserAccountMapped[] => {
        const userAccounts: {
            [key: string]: UserAccountRecord;
        } =
        (users[userId]?.UserAccounts ?? []).reduce((accumulator, value: string) => {
            const userAccount = userAccountsList[value];
            if (!userAccount?.IsDeleted && ClientRoleEnum.NOT_ASSIGNED !== userAccount?.ClientRoleId && currentUser.IsAdmin) {
                accumulator[value] = userAccount;
            }

            return accumulator;
        }, {});

        const mappedAccounts: UserAccountMapped[] = Object.values(userAccounts)
            .reduce((accumulator: UserAccountMapped[], userAccount: UserAccountRecord) => {
                const user = users[userAccount.UserId];
                const account = accounts[userAccount.AccountId];
                const firm = firms[(account || {}).FirmId];

                if (!account?.IsDeleted && (includeClosed || (!includeClosed && !account?.IsClosed)) && !user?.IsDeleted) {
                    accumulator.push({
                        userAccount,
                        user: user,
                        firm: firm,
                        account: account
                    });
                }

                return accumulator;
            }, []);

        return sort ? mappedAccounts.sort((a, b) => caseInsensitiveSort(a.account.AccountName, b.account.AccountName)) : mappedAccounts;
    }
);

export const getMappedUserAccountsByUserIdClient = createSelector(
    [ getUserAccounts, getAccountRelationships, getUser, getUsers, getAccounts, getFirms, getUserId, getIncludeClosed, getSort ],
    (userAccountsList, accountRelationshipsList, currentUser, users,
        accounts, firms, userId, includeClosed, sort): UserAccountMapped[] => {
        const userAccounts: {
            [key: string]: UserAccountRecord;
        } =
        (users[userId]?.UserAccounts ?? []).reduce((accumulator, value: string) => {
            const userAccount = userAccountsList[value];
            if (!userAccount?.IsDeleted && ClientRoleEnum.NOT_ASSIGNED !== userAccount?.ClientRoleId && !currentUser.IsAdmin) {
                accumulator[value] = userAccount;
            }

            return accumulator;
        }, {});

        const mappedAccounts: UserAccountMapped[] = Object.values(userAccounts)
            .reduce((accumulator: UserAccountMapped[], userAccount: UserAccountRecord) => {
                const user = users[userAccount.UserId];
                const account = accounts[userAccount.AccountId];
                const firm = firms[(account || {}).FirmId];

                const relatedAccounts = Object.keys(accountRelationshipsList)
                    .map(id => accountRelationshipsList[id])
                    .filter(relationship => relationship.AccountId2 === account.AccountId ||
                        relationship.AccountId1 === account.AccountId);

                if (!account?.IsDeleted && (includeClosed || (!includeClosed && !account?.IsClosed)) && !user?.IsDeleted) {
                    accumulator.push({
                        userAccount,
                        user: user,
                        firm: firm,
                        account: account,
                        linkedBDAccountId: relatedAccounts[0]?.AccountId1 && account.AccountId !== relatedAccounts[0].AccountId1 ?
                            relatedAccounts[0].AccountId1 : relatedAccounts[0]?.AccountId2
                    });
                }

                return accumulator;
            }, []);

        return sort ? mappedAccounts.sort((a, b) => caseInsensitiveSort(a.account.AccountName, b.account.AccountName)) : mappedAccounts;
    }
);

export const getMappedUserAccountsByUserId = createSelector(
    [ getUserAccounts, getAccountRelationships, getUser, getUsers, getAccounts, getFirms, getUserId, getIncludeClosed, getSort ],
    (userAccountsList, accountRelationshipsList, currentUser, users,
        accounts, firms, userId, includeClosed, sort): UserAccountMapped[] => {
        const clientUser = users[userId];
        const userAccounts: {
            [key: string]: UserAccountRecord;
        } =
        (clientUser?.UserAccounts ?? []).reduce((accumulator, value: string) => {
            const userAccount = userAccountsList[value];
            if (!clientUser.IsDeleted && !userAccount?.IsDeleted && ClientRoleEnum.NOT_ASSIGNED !== userAccount?.ClientRoleId &&
                !clientUser.IsAdmin) {
                accumulator[value] = userAccount;
            }

            return accumulator;
        }, {});

        const mappedAccounts: UserAccountMapped[] = Object.values(userAccounts)
            .reduce((accumulator: UserAccountMapped[], userAccount: UserAccountRecord) => {
                const user = users[userAccount.UserId];
                const account = accounts[userAccount.AccountId];
                const firm = firms[(account || {}).FirmId];

                const relatedAccounts = Object.keys(accountRelationshipsList)
                    .map(id => accountRelationshipsList[id])
                    .filter(relationship => relationship.AccountId2 === account.AccountId ||
                        relationship.AccountId1 === account.AccountId);

                const linkedBDAccountId = account.AccountId !== relatedAccounts[0].AccountId1 ?
                    relatedAccounts[0].AccountId1 : relatedAccounts[0].AccountId2;

                const linkedAccountNumber = accounts[linkedBDAccountId].Account;

                if (!account?.IsDeleted && (includeClosed || (!includeClosed && !account?.IsClosed)) && !user?.IsDeleted) {
                    accumulator.push({
                        userAccount,
                        user: user,
                        firm: firm,
                        account: account,
                        linkedAccountNumber: linkedAccountNumber
                    });
                }

                return accumulator;
            }, []);

        return sort ? mappedAccounts.sort((a, b) => caseInsensitiveSort(a.account.AccountName, b.account.AccountName)) : mappedAccounts;
    }
);

export const getMappedWalletUserAccountsByAccountId = createSelector(
    [ getUserAccounts, getUsers, getAccounts, getFirms, getAccountId, getIncludeClosed ],
    (userAccountsList, users, accounts, firms, accountId, includeClosed): UserAccountMapped[] => {
        const userAccounts: {
            [key: string]: UserAccountRecord;
        } = (accounts[accountId]?.UserAccounts ?? []).reduce((accumulator, value: string) => {
            const userAccount = userAccountsList[value];

            if (!userAccount?.IsDeleted && ClientRoleEnum.NOT_ASSIGNED !== userAccount?.ClientRoleId) {
                accumulator[value] = userAccount;
            }

            return accumulator;
        }, {});

        const mappedAccounts: UserAccountMapped[] = Object.values(userAccounts)
            .reduce((accumulator: UserAccountMapped[], userAccount: UserAccountRecord) => {
                const user = users[userAccount.UserId];
                const account = accounts[userAccount.AccountId];
                const firm = firms[(account || {}).FirmId];

                if (!account?.IsDeleted && !user?.IsDeleted && (includeClosed || (!includeClosed && !account?.IsClosed))) {
                    accumulator.push({
                        userAccount,
                        user: user,
                        firm: firm,
                        account: account
                    });
                }

                return accumulator;
            }, []);

        return mappedAccounts;
    }
);

export const getMappedUserAccountByUserAndAccountId = createSelector(
    [ getUserAccounts, getUsers, getAccounts, getFirms, getUserId, getAccountId ],
    (userAccountsList, users, accounts, firms, userId, accountId): UserAccountMapped => {
        const userAccounts = Object.keys(userAccountsList)
            .map(id => userAccountsList[id])
            .filter(userAccount => !userAccount.IsDeleted && userAccount.UserId === userId && userAccount.AccountId === accountId);

        if (userAccounts.length !== 1) return;
        else {
            const user = users[userAccounts[0].UserId];
            const account = accounts[userAccounts[0].AccountId];
            if (!user || !account) return;

            const firm = firms[account.FirmId];
            if (!firm) return;

            else return {
                userAccount: userAccounts[0],
                user: user,
                firm: firm,
                account: account
            };
        }
    }
);

const getMappedUserAccountsList = createSelector(
    [ getUserAccounts, getUsers, getAccounts, getFirms, getFirmId ],
    (userAccountsList, users, accounts, firms, firmId): UserAccountMappedObj => {
        const userList = Object.keys(users)
            .filter(UserId => (!users[UserId].IsAdmin && !users[UserId].IsDeleted && (!firmId || firmId === users[UserId].FirmId)))
            .map(UserId => users[UserId]);

        return userList.reduce((acc, user) => {
            const userAccounts = Object.keys(userAccountsList)
                .map(id => userAccountsList[id])
                .filter(userAccount => !userAccount.IsDeleted && userAccount.UserId === user.UserId);

            const mappedAccounts = userAccounts.map(userAccount => {
                const user = users[userAccount.UserId];
                const account = accounts[userAccount.AccountId];
                const firm = firms[(account || {}).FirmId];

                return {
                    userAccount: userAccount,
                    user: user,
                    firm: firm,
                    account: account
                };
            })
                .filter(mappedUserAccount => mappedUserAccount.account &&
                    mappedUserAccount.user &&
                    mappedUserAccount.userAccount &&
                    !mappedUserAccount.account.IsDeleted &&
                    !mappedUserAccount.account.IsClosed &&
                    !mappedUserAccount.user.IsDeleted);

            acc[user.UserId] = mappedAccounts;

            return acc;
        }, {});
    }
);

const getUserAccountsByUserId = createSelector(
    [ getUserAccounts, getAccounts, getUserIdWithFirm ],
    (userAccountsList, accounts, userId): { account: AccountRecordExtended; userAccount: UserAccountRecord }[] => {
        const userAccounts = Object.keys(userAccountsList)
            .map(id => userAccountsList[id])
            .filter(userAccount => !userAccount.IsDeleted && userAccount.UserId === userId);

        return userAccounts.map(userAccount => {
            const account = accounts[userAccount.AccountId];

            return {
                account,
                userAccount
            };
        })
            .filter(mappedUserAccount => !mappedUserAccount.account.IsDeleted);
    }
);

export const getUnmappedTokenWalletsByUserId = createSelector(
    [ getUsers, getOpenWalletssByFirmId, getUserAccountsByUserId, getUserIdWithFirm ],
    (users, openAccounts, userAccounts, userId): UserAccountMapped[] => {
        const user = users[userId];

        const allOpenAccounts = openAccounts;

        return allOpenAccounts.map((tokenAccount) => {
            if (tokenAccount.IsDeleted) return;

            let tempAccount = {};

            const exsistingNonMappedAccount = userAccounts.filter((account) => {
                if (account.account.AccountId === tokenAccount.AccountId &&
                    account.userAccount.ClientRoleId === ClientRoleEnum.NOT_ASSIGNED &&
                    account.account.AccountTypeId === AccountType.CLIENT_EOA) {
                    tempAccount = account.userAccount;
                }
                if (account.account.AccountId === tokenAccount.AccountId &&
                    account.userAccount.ClientRoleId !== ClientRoleEnum.NOT_ASSIGNED &&
                    account.account.AccountTypeId === AccountType.CLIENT_EOA) {
                    return account;
                }
            });

            return exsistingNonMappedAccount.length > 0 ?
                null
                : {
                    user,
                    account: tokenAccount,
                    userAccount: tempAccount as UserAccountRecord
                };
        })
            .filter(noNull => noNull);
    }
);

export const getUnmappedUsersByAccountId = createSelector(
    [ getAccounts, getAccountId, getUserAccounts, getUsers, getMappedUserAccountsList ],
    (accounts, accountId, userAccountsList, users, mappedUserAccountsList): UserAccountMapped[] => {
        const account = accounts[accountId];
        if (account.IsDeleted) return [];

        const firmUsers = Object.keys(users)
            .map((id) => users[id])
            .filter((user) => {
                const userAccountList = mappedUserAccountsList[user.UserId];

                return !user.IsDeleted && !user.IsAdmin && user.FirmId === account.FirmId;
            });

        return firmUsers.map((user) => {
            const userAccounts = Object.keys(userAccountsList)
                .map((id) => userAccountsList[id])
                .filter((userAccount) => !userAccount.IsDeleted && userAccount.UserId === user.UserId);
            const userAccountRecords = userAccounts
                .map((userAccount) => {
                    const account = accounts[userAccount.AccountId];

                    return {
                        account,
                        userAccount
                    };
                })
                .filter(mappedUserAccount => !mappedUserAccount.account.IsDeleted);

            const emptyUserAccount = {
                user,
                account,
                userAccount: {} as UserAccountRecord
            };

            if (userAccountRecords.length === 0) {
                return emptyUserAccount;
            }

            let tempAccount = {};
            const userAccountRecordObj = userAccountRecords.filter((UAR) => {
                if (UAR.account.AccountId === account.AccountId && UAR.userAccount.ClientRoleId === ClientRoleEnum.NOT_ASSIGNED) {
                    tempAccount = UAR.userAccount;
                }
                if (UAR.account.AccountId === account.AccountId && UAR.userAccount.ClientRoleId !== ClientRoleEnum.NOT_ASSIGNED) {
                    return account;
                }
            });

            return userAccountRecordObj.length > 0 ? null : {
                user,
                account: account,
                userAccount: tempAccount as UserAccountRecord
            };
        })
            .filter(userObj => userObj)
            .flat();
    }
);

export const getUserAccountByTransaction = createSelector(
    [ getTransaction, getOpenWalletsArray, getUser, getUserAccounts ],
    (transaction, accounts, user, userAccountsList): UserAccountRecord => {
        if (!transaction) return {} as UserAccountRecord;
        if (user && user.IsAdmin) return {} as UserAccountRecord;

        const userAccounts = Object.keys(userAccountsList)
            .map(id => userAccountsList[id])
            .filter(userAccount => !userAccount.IsDeleted);

        let walletAddress = '';
        if (transaction.type === TransactionTypeChar.transfer ||
            transaction.type === TransactionTypeChar.send ||
            transaction.type === TransactionTypeChar.deposit ||
            transaction.type === TransactionTypeChar.order) {
            walletAddress = transaction.SenderAddress;
        } else if (transaction.type === TransactionTypeChar.receive ||
            transaction.type === TransactionTypeChar.withdrawal ||
            transaction.type === TransactionTypeChar.int_distribution) {
            walletAddress = transaction.RecipientAddress;
        }

        const account = accounts.find(acc => acc.Account === walletAddress);
        if (!account) return {} as UserAccountRecord;

        return userAccounts.find((userAccount: UserAccountRecord) => userAccount.UserId === user.UserId &&
            userAccount.AccountId === account.AccountId) || {} as UserAccountRecord;
    }
);

export const getUserAccountByPledgeParticipantAccount = createSelector(
    [ getPledgeParticipantAccount, getOpenWalletsArray, getUser, getUserAccounts ],
    (pledgeParticipantAccount, accounts, user, userAccountsList): UserAccountRecord | undefined => {
        if (!pledgeParticipantAccount) return undefined;
        if (user && user.IsAdmin) return undefined;

        const userAccounts = Object.keys(userAccountsList)
            .map(id => userAccountsList[id])
            .filter(userAccount => !userAccount.IsDeleted);

        const account = accounts.find(acc => acc.Account === pledgeParticipantAccount);

        if (!account) return undefined;

        return userAccounts.find((userAccount: UserAccountRecord) => userAccount.UserId === user.UserId &&
            userAccount.AccountId === account.AccountId);
    }
);

export const getClientWithUserAccounts = createSelector(
    [ getState, getUsers, getFirms, getUserSearchString ],
    (state, users, firms, searchValue): {
        searchResults: ClientUserMapped[];
        isTrimmed: boolean;
        resultTotal: number;
    } => {
        let clientUsers = Object.keys(users)
            .filter(UserId => (
                !users[UserId].IsAdmin &&
                !users[UserId].IsDeleted &&
                users[UserId].Email.includes(searchValue)
            ))
            .map(UserId => users[UserId]);

        const resultTotal = clientUsers.length;
        let isTrimmed = false;
        if (resultTotal > MAX_SEARCH_RESULTS) {
            clientUsers = clientUsers.slice(0, MAX_SEARCH_RESULTS);
            isTrimmed = true;
        }

        const mappedUsers = clientUsers.map(user => {
            const firm = firms[user.FirmId];
            const accounts = getMappedUserAccountsByUserIdClient(state, user.UserId, undefined, undefined, undefined);

            return {
                user,
                firm,
                accounts
            };
        })
            .filter(mappedUser => !mappedUser.firm.IsDeleted);

        return {
            searchResults: mappedUsers,
            resultTotal,
            isTrimmed
        };
    }
);